import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus, Check, X, Clock, AlertTriangle, User, Users,
    Trash2, RotateCcw, CalendarClock, ListChecks,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useRuoli, etichettaRuolo } from '../utils/ruoli';
import { useAuthStore } from '../store/useStore';
import { vaiA, haDestinazione, ETICHETTA } from '../utils/destinazione';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';
import RiferimentoSelect from '../components/RiferimentoSelect';
import DestinatarioSelect from '../components/DestinatarioSelect';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import { useTurno } from '../components/codaFinestre';
import '../styles/Compiti.css';

const VISTE = [
    { chiave: 'aperto',     etichetta: 'Da fare' },
    { chiave: 'completato', etichetta: 'Fatti' },
    { chiave: 'tutti',      etichetta: 'Tutti' },
];

/**
 * Compiti — quello che c'è da fare, e chi lo deve fare.
 *
 * Separati dai promemoria clinici: quelli nascono sul paziente e
 * riguardano la cura, questi riguardano l'organizzazione della
 * giornata.
 *
 * Un compito assegnato a un ruolo lo chiude chiunque, e da quel
 * momento è chiuso per tutti: se è "segreteria" non interessa chi lo
 * fa, interessa che sia fatto.
 */

/* I ruoli arrivano da /api/ruoli, non da un elenco scritto qui: vedi
   utils/ruoli.js. Le etichette sono quelle della fonte - «Medico
   senior», al singolare - in ogni schermata. */

/* Un compito che ho scritto per qualcun altro sta nel mio elenco solo
   perche' ne veda l'esito. Senza distinguerlo, la lista di chi assegna
   molto diventa un elenco di cose che sembrano da fare a lui. */
const soloDaSeguire = (c, utente) => (
    c.creato_da === utente?.id
    && c.assegnato_a !== utente?.id
    && c.assegnato_ruolo !== utente?.ruolo
);

const quando = (d) => {
    if (!d) return null;
    const data = new Date(d);
    const oggi = new Date();
    const domani = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate() + 1);
    const ora = data.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

    if (data.toDateString() === oggi.toDateString()) return `oggi alle ${ora}`;
    if (data.toDateString() === domani.toDateString()) return `domani alle ${ora}`;
    return data.toLocaleDateString('it-IT', {
        day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
    });
};

const Compiti = () => {
    const navigate = useNavigate();
    const utente = useAuthStore((s) => s.user);
    const { ruoli } = useRuoli();
    const nomeRuolo = (r) => etichettaRuolo(ruoli, r);
    const [stato, setStato] = useState('aperto');
    const [compiti, setCompiti] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [scheda, setScheda] = useState(null);   // null | 'nuovo' | compito

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await api.get('/compiti', { params: { stato } });
            setCompiti(r.data.data.compiti || []);
            setErrore(null);
        } catch (e) {
            setErrore(e.response?.data?.message || 'Non riesco a caricare i compiti');
        } finally {
            setCaricamento(false);
        }
    }, [stato]);

    useEffect(() => { carica(); }, [carica]);

    const completa = async (c) => {
        try {
            await api.post(`/compiti/${c.id}/completa`);
            carica();
        } catch (e) {
            setErrore(e.response?.data?.message || 'Non riesco a chiudere il compito');
        }
    };

    const riapri = async (c) => {
        try {
            await api.post(`/compiti/${c.id}/riapri`);
            carica();
        } catch (e) { setErrore('Non riesco a riaprire il compito'); }
    };

    const elimina = async (c) => {
        if (!window.confirm('Eliminare questo compito?')) return;
        try {
            await api.delete(`/compiti/${c.id}`);
            carica();
        } catch (e) { setErrore('Non riesco a eliminare il compito'); }
    };

    return (
        <div className="cmp-wrap">
            <IntestazioneSezione
                titolo="Compiti"
                icona={<ListChecks size={20} aria-hidden="true" />}
                sottotitolo="Cose da fare e passaggio di consegne."
                viste={VISTE}
                vista={stato}
                onVista={setStato}
                azione={
                    <button className="cmp-btn primario" onClick={() => setScheda('nuovo')}>
                        <Plus size={16} /> Nuovo compito
                    </button>
                }
            />

            {errore && <div className="cmp-errore">{errore}</div>}
            {caricamento && !compiti.length && <p className="cmp-vuoto">Caricamento…</p>}

            {!caricamento && !compiti.length && (
                <p className="cmp-vuoto">
                    {stato === 'aperto'
                        ? 'Niente da fare. Buon lavoro.'
                        : 'Nessun compito in questo elenco.'}
                </p>
            )}

            <ul className="cmp-elenco">
                {compiti.map((c) => (
                    <li key={c.id} className={`cmp-riga ${c.stato} ${c.scaduto ? 'scaduto' : ''}`}>
                        <button
                            className="cmp-spunta"
                            title={c.stato === 'aperto' ? 'Segna come fatto' : 'Riapri'}
                            onClick={() => (c.stato === 'aperto' ? completa(c) : riapri(c))}
                        >
                            {c.stato === 'aperto' ? <span className="cmp-cerchio" />
                                : <Check size={15} />}
                        </button>

                        <div className="cmp-corpo" onClick={() => setScheda(c)}>
                            <div className="cmp-titolo">
                                {c.priorita === 'alta' && (
                                    <AlertTriangle size={14} className="cmp-urgente" />
                                )}
                                <b>{c.titolo}</b>
                            </div>

                            {c.descrizione && <p className="cmp-desc">{c.descrizione}</p>}

                            <div className="cmp-meta">
                                <span className="cmp-a-chi">
                                    {c.assegnato_ruolo
                                        ? <><Users size={12} /> {nomeRuolo(c.assegnato_ruolo)}</>
                                        : (
                                            <>
                                                <User size={12} />
                                                {c.assegnato_cognome
                                                    ? `${c.assegnato_cognome} ${c.assegnato_nome}`
                                                    : 'assegnato'}
                                            </>
                                        )}
                                </span>

                                {soloDaSeguire(c, utente) && (
                                    <span className="cmp-chiesto">chiesto da te</span>
                                )}

                                {c.scadenza && (
                                    <span className={c.scaduto ? 'cmp-scaduto' : ''}>
                                        <Clock size={12} /> {quando(c.scadenza)}
                                    </span>
                                )}

                                {c.paziente_cognome && (
                                    <button
                                        className="cmp-paziente"
                                        title={haDestinazione(c)
                                            ? `Apri ${ETICHETTA[c.riferimento_tipo]}`
                                            : 'Apri la scheda del paziente'}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            vaiA(navigate, c);
                                        }}
                                    >
                                        {c.paziente_cognome} {c.paziente_nome}
                                        {haDestinazione(c) && (
                                            <span className="cmp-verso">
                                                › {ETICHETTA[c.riferimento_tipo]}
                                            </span>
                                        )}
                                    </button>
                                )}

                                {/* Un compito di servizio senza paziente (i controlli
                                    automatici, le iscrizioni all'albo): il pulsante
                                    porta alla schermata dove si lavora. Senza, il
                                    compito diceva cosa fare e non dove. */}
                                {!c.paziente_cognome && haDestinazione(c) && (
                                    <button
                                        className="cmp-paziente"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            vaiA(navigate, c);
                                        }}
                                    >
                                        Apri {ETICHETTA[c.riferimento_tipo]}
                                    </button>
                                )}

                                {c.stato === 'completato' && c.chiuso_da_cognome && (
                                    <span className="cmp-chiuso">
                                        fatto da {c.chiuso_da_cognome}
                                    </span>
                                )}
                            </div>
                        </div>

                        <button className="cmp-elimina" title="Elimina"
                                onClick={() => elimina(c)}>
                            <Trash2 size={14} />
                        </button>
                    </li>
                ))}
            </ul>

            {scheda && (
                <SchedaCompito
                    compito={scheda === 'nuovo' ? null : scheda}
                    onChiudi={() => setScheda(null)}
                    onSalvato={() => { setScheda(null); carica(); }}
                    onErrore={setErrore}
                />
            )}
        </div>
    );
};

/* ---------- scheda di inserimento e modifica ---------- */

const SchedaCompito = ({ compito, onChiudi, onSalvato, onErrore }) => {
    const { ruoli } = useRuoli();
    const [v, setV] = useState({
        titolo: compito?.titolo || '',
        descrizione: compito?.descrizione || '',
        scadenza: compito?.scadenza
            ? new Date(compito.scadenza).toISOString().slice(0, 16) : '',
        priorita: compito?.priorita || 'normale',
        a_chi: compito?.assegnato_a ? `u${compito.assegnato_a}`
            : (compito?.assegnato_ruolo || 'segreteria'),
        paziente_id: compito?.paziente_id || null,
    });
    const [utenti, setUtenti] = useState([]);
    const [cercaPaziente, setCercaPaziente] = useState('');
    const [pazienti, setPazienti] = useState([]);
    const [pazienteScelto, setPazienteScelto] = useState(
        compito?.paziente_cognome
            ? { id: compito.paziente_id, cognome: compito.paziente_cognome, nome: compito.paziente_nome }
            : null
    );
    const [rifTipo, setRifTipo] = useState(compito?.riferimento_tipo || null);
    const [rifId, setRifId] = useState(compito?.riferimento_id || null);
    /* Chiudere con qualcosa di diverso da com'era chiede conferma: sfondo,
       X e Annulla passano da chiudi(). I dati iniziali vengono dalle
       props: l'istantanea e' quella del montaggio. */
    const sporco = useSporco({ v, pazienteScelto: pazienteScelto?.id || null, rifTipo, rifId }, 0);
    const chiedi = useUscitaProtetta(sporco);
    const chiudi = () => { if (chiedi()) onChiudi(); };
    const [inCorso, setInCorso] = useState(false);

    useEffect(() => {
        api.get('/utenti')
            .then((r) => {
                const d = r.data.data;
                setUtenti(Array.isArray(d) ? d : (d?.utenti || []));
            })
            .catch(() => setUtenti([]));   // non admin: resta la scelta per ruolo
    }, []);

    useEffect(() => {
        if (cercaPaziente.trim().length < 3) { setPazienti([]); return undefined; }
        let attivo = true;
        const t = setTimeout(async () => {
            try {
                const r = await api.get('/pazienti', {
                    params: { search: cercaPaziente, limit: 8 },
                });
                if (attivo) setPazienti(r.data.data?.pazienti || []);
            } catch (e) { if (attivo) setPazienti([]); }
        }, 300);
        return () => { attivo = false; clearTimeout(t); };
    }, [cercaPaziente]);

    const salva = async () => {
        if (!v.titolo.trim()) { onErrore('Scrivi cosa c\'è da fare'); return; }
        try {
            setInCorso(true);
            /* Il campo unico "a chi" si sdoppia qui: gli utenti sono
               prefissati con u, tutto il resto è un ruolo. */
            const corpo = {
                titolo: v.titolo.trim(),
                descrizione: v.descrizione.trim() || null,
                scadenza: v.scadenza || null,
                priorita: v.priorita,
                paziente_id: pazienteScelto?.id || null,
                /* Senza paziente non c'e' destinazione da seguire, e il
                   database rifiuterebbe il riferimento orfano. */
                riferimento_tipo: pazienteScelto ? rifTipo : null,
                riferimento_id: pazienteScelto ? rifId : null,
                assegnato_a: v.a_chi.startsWith('u') ? parseInt(v.a_chi.slice(1), 10) : null,
                assegnato_ruolo: v.a_chi.startsWith('u') ? null : v.a_chi,
            };
            if (compito) await api.put(`/compiti/${compito.id}`, corpo);
            else await api.post('/compiti', corpo);
            onSalvato();
        } catch (e) {
            onErrore(e.response?.data?.message || 'Non riesco a salvare');
        } finally {
            setInCorso(false);
        }
    };

    return (
        <div className="cmp-modale-fondo" onClick={chiudi}>
            <div className="cmp-modale" onClick={(e) => e.stopPropagation()}>
                <header>
                    <h3>{compito ? 'Modifica il compito' : 'Nuovo compito'}</h3>
                    <button onClick={chiudi}><X size={18} /></button>
                </header>

                <label className="cmp-campo">
                    <span>Cosa c&apos;è da fare</span>
                    <input
                        autoFocus
                        value={v.titolo}
                        onChange={(e) => setV({ ...v, titolo: e.target.value })}
                        placeholder="Ordinare i filler, richiamare la signora…"
                    />
                </label>

                <label className="cmp-campo">
                    <span>Dettagli (facoltativo)</span>
                    <textarea
                        rows={2}
                        value={v.descrizione}
                        onChange={(e) => setV({ ...v, descrizione: e.target.value })}
                    />
                </label>

                <div className="cmp-campi-doppi">
                    <div className="cmp-campo">
                        <span>A chi</span>
                        <DestinatarioSelect
                            value={v.a_chi}
                            onChange={(x) => setV({ ...v, a_chi: x })}
                            ruoli={ruoli}
                            utenti={utenti}
                            prefisso="compatto"
                        />
                    </div>

                    <label className="cmp-campo cmp-stretto">
                        <span>Priorità</span>
                        <select value={v.priorita}
                                onChange={(e) => setV({ ...v, priorita: e.target.value })}>
                            <option value="bassa">Bassa</option>
                            <option value="normale">Normale</option>
                            <option value="alta">Alta</option>
                        </select>
                    </label>
                </div>

                <label className="cmp-campo">
                    <span>Entro quando (facoltativo)</span>
                    <input
                        type="datetime-local"
                        value={v.scadenza}
                        onChange={(e) => setV({ ...v, scadenza: e.target.value })}
                    />
                    <em className="cmp-nota">
                        Senza scadenza il compito resta in elenco senza interrompere nessuno.
                    </em>
                </label>

                <div className="cmp-campo">
                    <span>Riguarda un paziente? (facoltativo)</span>
                    {pazienteScelto ? (
                        <div className="cmp-paziente-scelto">
                            <span>{pazienteScelto.cognome} {pazienteScelto.nome}</span>
                            <button onClick={() => {
                                setPazienteScelto(null); setCercaPaziente('');
                                setRifTipo(null); setRifId(null);
                            }}>
                                <X size={14} />
                            </button>
                        </div>
                    ) : (
                        <>
                            <input
                                value={cercaPaziente}
                                onChange={(e) => setCercaPaziente(e.target.value)}
                                placeholder="Cerca: almeno tre lettere…"
                            />
                            {pazienti.length > 0 && (
                                <ul className="cmp-risultati">
                                    {pazienti.map((p) => (
                                        <li key={p.id}>
                                            <button onClick={() => {
                                                setPazienteScelto(p);
                                                setPazienti([]);
                                            }}>
                                                {p.cognome} {p.nome}
                                                <em>{p.codice_fiscale || ''}</em>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>

                {/* La destinazione compare solo con un paziente scelto: senza,
                    non c'e' scheda dentro cui atterrare. */}
                <RiferimentoSelect
                    pazienteId={pazienteScelto?.id || null}
                    tipo={rifTipo}
                    id={rifId}
                    onChange={(t, i) => { setRifTipo(t); setRifId(i); }}
                    classe="cmp-campo"
                />

                <footer>
                    <button className="cmp-btn" onClick={chiudi}>Annulla</button>
                    <button className="cmp-btn primario" onClick={salva} disabled={inCorso}>
                        {compito ? 'Salva' : 'Crea'}
                    </button>
                </footer>
            </div>
        </div>
    );
};

/* ==========================================================
   Popup all'apertura — esportato a parte
   ========================================================== */

/* «Più tardi»: dopo quanto la finestra dei compiti urgenti si rivede. Un'ora:
   abbastanza per finire la visita o la fila allo sportello, non tanto da
   lasciare un compito urgente fuori dalla vista per mezza giornata. Il rinvio
   e' del browser e della persona (chiave con l'id), non del compito: sul
   server non cambia niente. */
export const RIMANDO_MINUTI = 60;
const chiaveRimando = (id) => `compiti_popup_rimandato_${id}`;
const rimandoDi = (id) => {
    try {
        const v = Number(localStorage.getItem(chiaveRimando(id)));
        return Number.isFinite(v) && v > 0 ? v : null;
    } catch (e) { return null; }
};
const segnaRimando = (id, fino) => {
    try { localStorage.setItem(chiaveRimando(id), String(fino)); } catch (e) { /* modalita' privata: si chiude e basta */ }
};
const togliRimando = (id) => {
    try { localStorage.removeItem(chiaveRimando(id)); } catch (e) { /* pazienza */ }
};

export const PopupCompiti = () => {
    const [compiti, setCompiti] = useState([]);
    const [indice, setIndice] = useState(0);

    /* Il Layout viene rimontato a ogni cambio di schermata: senza
       questo controllo la finestra si riaffaccerebbe a ogni clic del
       menu, e un avviso che compare sempre smette di essere un avviso.
       Chiave di sessione, non di giorno: la sessione finisce con la
       scheda del browser, quindi domani mattina si rivede. */
    const utenteId = useAuthStore((s) => s.user?.id);
    const [giro, setGiro] = useState(0);

    useEffect(() => {
        if (!utenteId) return undefined;
        const chiedi = () => {
            setIndice(0);
            api.get('/compiti/da-mostrare')
                .then((r) => setCompiti(r.data.data.compiti || []))
                .catch(() => setCompiti([]));
        };
        /* «Più tardi» ha la precedenza sulla sessione: finche' il rinvio non
           scade la finestra tace, poi si rifa' viva da sola - anche nella
           stessa sessione, anche se nel frattempo si e' cambiata schermata
           (il Layout si rimonta e il conto riparte dall'ora salvata). */
        const fino = rimandoDi(utenteId);
        if (fino) {
            const manca = fino - Date.now();
            if (manca > 0) {
                /* Si chiede direttamente: rimettere in moto l'effetto non
                   basterebbe, la sessione e' gia' segnata e tacerebbe. */
                const t = setTimeout(() => { togliRimando(utenteId); chiedi(); }, Math.min(manca, 2 ** 31 - 1));
                return () => clearTimeout(t);
            }
            togliRimando(utenteId);
            chiedi();
            return undefined;
        }
        let giaChiesto = false;
        try { giaChiesto = sessionStorage.getItem('compiti_popup') === '1'; } catch (e) { /* modalita' privata */ }
        if (giaChiesto) return undefined;
        try { sessionStorage.setItem('compiti_popup', '1'); } catch (e) { /* pazienza */ }
        chiedi();
        return undefined;
    }, [utenteId, giro]);

    const corrente = compiti[indice];
    /* Una finestra alla volta (components/codaFinestre.js): i compiti urgenti
       hanno la precedenza, ma aspettano comunque il loro turno. */
    const mio = useTurno('compiti', !!corrente);
    if (!corrente || !mio) return null;

    const avanti = () => setIndice((i) => i + 1);

    const fatto = async () => {
        try { await api.post(`/compiti/${corrente.id}/completa`); } catch (e) { /* pazienza */ }
        avanti();
    };

    const rinvia = async (minuti) => {
        try {
            await api.post(`/compiti/${corrente.id}/rinvia`, { minuti });
        } catch (e) { /* pazienza */ }
        avanti();
    };

    const piuTardi = () => {
        segnaRimando(utenteId, Date.now() + RIMANDO_MINUTI * 60000);
        setCompiti([]);
        setGiro((g) => g + 1);
    };

    const dopo = async () => {
        /* "Lo vedo dopo": non lo si mostra più oggi, ma resta aperto. */
        try { await api.post(`/compiti/${corrente.id}/visto`); } catch (e) { /* pazienza */ }
        avanti();
    };

    return (
        <div className="cmp-popup-fondo">
            <div className="cmp-popup">
                <div className="cmp-popup-testa">
                    <CalendarClock size={18} />
                    <span>
                        {corrente.scaduto ? 'Era da fare' : 'Da fare oggi'}
                        {compiti.length > 1 && ` · ${indice + 1} di ${compiti.length}`}
                    </span>
                </div>

                <h3>{corrente.titolo}</h3>
                {corrente.descrizione && <p>{corrente.descrizione}</p>}

                {corrente.paziente_cognome && (
                    <p className="cmp-popup-paziente">
                        {corrente.paziente_cognome} {corrente.paziente_nome}
                    </p>
                )}

                <p className="cmp-popup-quando">
                    <Clock size={13} /> {quando(corrente.scadenza)}
                </p>

                <div className="cmp-popup-azioni">
                    <button className="cmp-btn primario" onClick={fatto}>
                        <Check size={15} /> Fatto
                    </button>
                    <button className="cmp-btn" onClick={() => rinvia(30)}>
                        <RotateCcw size={15} /> Fra mezz&apos;ora
                    </button>
                    <button className="cmp-btn" onClick={() => rinvia(120)}>
                        Fra due ore
                    </button>
                    <button className="cmp-btn chiaro" onClick={dopo}>
                        Lo vedo dopo
                    </button>
                    {/* «Più tardi» rimanda la FINESTRA, non il compito (Alex,
                        1/10/2026): nessuna chiamata al server, il compito resta
                        aperto e la finestra si rivede dopo RIMANDO_MINUTI.
                        «Fra mezz'ora» e «Fra due ore» invece spostano il
                        compito, «Lo vedo dopo» scrive che e' stato visto oggi. */}
                    <button className="cmp-btn chiaro" data-azione="piu-tardi" onClick={piuTardi}>
                        Più tardi
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Compiti;
